// PlaylistStrip — thumbnail navigation for playlist-scoped share links
// (PRD §9.5 + docs/frontend_architecture.md §6, B2 PR6 / B2f).
//
// Surface: one tile per playlist item, rendered below the video. Active
// tile is highlighted (border accent + aria-pressed=true). Tapping a
// tile invokes `onSelect(candidateId)` — the parent owns the active
// state, so tile clicks never trigger a router transition; the active
// candidate swap is a local re-render of the panel above.
//
// Thumbnail selection rule:
//   1. First asset with `mime_type` starting with `image/` - that's
//      the thumbnail still the publish path stamps for each
//      candidate.
//   2. Fallback: if no still exists, render a label-only tile (the
//      proxy-poster path would require a video-frame extraction the
//      backend doesn't yet do for share-link thumbnails).
//
// Each tile is its own data-fetch (`useShareCandidate` + a single
// `useShareAssetUrl` for the chosen thumbnail asset). With TanStack
// Query caching this is O(N) fetches on first paint, then free on
// subsequent navigation — and it keeps the strip resilient to
// large playlists where bundling all candidates into one response
// would balloon the payload.

import { useMemo } from 'react';

import { useShareAssetUrl, useShareCandidate } from '../api/queries/share';
import type { components } from '../api/schema';
import { pickThumbnailAsset } from './playlistThumbs';

type PlaylistItemOut = components['schemas']['PlaylistItemOut'];

interface PlaylistStripProps {
  token: string;
  items: PlaylistItemOut[];
  activeCandidateId: string;
  onSelect: (candidateId: string) => void;
  password?: string | null;
}

export function PlaylistStrip({
  token,
  items,
  activeCandidateId,
  onSelect,
  password = null,
}: PlaylistStripProps): React.JSX.Element {
  // Items arrive from the access response sorted by `position` (the
  // service guarantees it), but defensive-sort here so the strip
  // doesn't reorder under us if that contract changes.
  const ordered = useMemo(
    () => [...items].sort((a, b) => a.position - b.position),
    [items]
  );

  if (ordered.length === 0) {
    return (
      <section
        aria-label="Playlist strip"
        className="border-border bg-bg-elevated/40 rounded-2 border border-dashed px-4 py-5"
      >
        <p className="text-text-faint text-13">This playlist is empty.</p>
      </section>
    );
  }

  return (
    <section
      aria-label="Playlist strip"
      // role="tablist" + tab semantics would be ideal but the active
      // panel above isn't a tabpanel (it's a full <section>). A plain
      // toolbar of buttons with aria-pressed is the truer match.
      className="border-border bg-bg-elevated/40 flex gap-2 overflow-x-auto rounded-2 border p-2"
      role="toolbar"
    >
      {ordered.map((item, idx) => (
        <PlaylistTile
          key={item.id}
          token={token}
          candidateId={item.candidate_id}
          position={idx + 1}
          active={item.candidate_id === activeCandidateId}
          onSelect={onSelect}
          password={password}
        />
      ))}
    </section>
  );
}

interface PlaylistTileProps {
  token: string;
  candidateId: string;
  position: number;
  active: boolean;
  onSelect: (candidateId: string) => void;
  password?: string | null;
}

function PlaylistTile({
  token,
  candidateId,
  position,
  active,
  onSelect,
  password = null,
}: PlaylistTileProps): React.JSX.Element {
  const candidate = useShareCandidate(token, candidateId, password);
  const thumb = pickThumbnailAsset(candidate.data);
  // Mint a presigned URL for the thumbnail asset (if we have one). The
  // hook is `enabled` only when we actually have an assetId, so it
  // doesn't fetch on label-only tiles.
  const thumbUrl = useShareAssetUrl(
    token,
    candidateId,
    thumb?.id,
    password
  );

  // Tile label: candidate.label when set, otherwise "Take {position}"
  // so the user has something to click before the fetch resolves.
  const label =
    candidate.data?.label && candidate.data.label.length > 0
      ? candidate.data.label
      : `Take ${position.toString()}`;

  const baseClass =
    'group relative flex h-16 w-24 shrink-0 cursor-pointer flex-col items-stretch overflow-hidden rounded-2 border text-left transition-colors';
  const activeClass = active
    ? 'border-accent ring-accent ring-1'
    : 'border-border hover:border-border-strong';

  return (
    <button
      type="button"
      onClick={() => {
        onSelect(candidateId);
      }}
      aria-pressed={active}
      aria-label={`Open ${label}`}
      className={`${baseClass} ${activeClass}`}
    >
      {thumbUrl.data !== undefined ? (
        <img
          src={thumbUrl.data.url}
          alt=""
          // Solid bg in case the image hasn't loaded yet; `loading=lazy`
          // so a long playlist doesn't fire all thumbnail requests at
          // once on initial paint.
          loading="lazy"
          className="bg-bg-muted absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="bg-bg-muted absolute inset-0" />
      )}
      <span className="text-text relative z-10 mt-auto truncate bg-black/40 px-1 py-0.5 text-11 font-medium">
        {label}
      </span>
    </button>
  );
}
